import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Layout from '../Layout/Layout';
import { AuthContext } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import { motion, AnimatePresence } from 'framer-motion';
import PaymentDetails from './PaymentDetails';

const Container = styled.div`
  padding: 24px;
  max-width: 1000px;
  margin: 0 auto;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const Title = styled.h1`
  font-size: 48px;
  text-align: center;
  margin-bottom: 16px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
`;

const Description = styled.p`
  color: var(--text-secondary);
  text-align: center;
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 48px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const PlanCard = styled.div`
  background: var(--surface);
  border-radius: 24px;
  padding: 40px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border);
  position: relative;
  overflow: hidden;
  animation: slideUp 0.5s ease-out;

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
  }
`;

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const PlanName = styled.h2`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 16px;
  color: var(--text-primary);
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const Price = styled.span`
  font-size: 48px;
  font-weight: 800;
  color: var(--primary);
`;

const Period = styled.span`
  font-size: 20px;
  color: var(--text-secondary);
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
  display: grid;
  gap: 16px;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  color: var(--text-primary);
  padding: 12px;
  border-radius: 12px;
  background: var(--background);
  transition: all var(--transition-normal);

  &:hover {
    transform: translateX(8px);
    background: var(--hover);
  }

  &::before {
    content: '✨';
    font-size: 20px;
  }
`;

const SubscribeButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: none;
  background: var(--gradient-primary);
  color: white;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  margin-bottom: 16px;
  box-shadow: var(--shadow-md);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:active {
    transform: translateY(0);
  }
`;

const ReturnButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);

  &:hover {
    background: var(--surface-hover);
    transform: translateY(-2px);
    border-color: var(--primary-light);
  }
`;

const PaymentModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow-y: auto;
`;

const ModalContent = styled(motion.div)`
  background: var(--background);
  border-radius: 24px;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: var(--surface);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  z-index: 1;
  color: var(--text-primary);
  
  &:hover {
    background: var(--hover);
  }
`;

const OrderSummary = styled.div`
  background: var(--background);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 24px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const SummaryLabel = styled.span`
  color: var(--text-secondary);
`;

const SummaryValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
`;

const PayButton = styled(SubscribeButton)`
  margin-top: 24px;
  margin-bottom: 0;
`;

const PaymentConfirmation = styled.div`
  margin-top: 24px;
  padding: 20px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
`;

const PaymentSummary = styled.div`
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border);

  h3 {
    font-size: 18px;
    margin-bottom: 12px;
    color: var(--text-primary);
  }

  p {
    display: flex;
    justify-content: space-between;
    margin: 8px 0;
    color: var(--text-secondary);
  }
`;

const Button = styled.button`
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all var(--transition-normal);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ConfirmButton = styled(SubscribeButton)`
  background: var(--success);
  margin-top: 16px;

  &:disabled {
    background: var(--disabled);
    cursor: not-allowed;
    transform: none;
  }
`;

const ProSubscription = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    name: '',
    email: user?.email || '',
    country: ''
  });
  const [error, setError] = useState(null);

  const features = [
    'Unlimited Subscriptions',
    'Advanced Analytics & Reports',
    'Custom Categories & Tags',
    'Email Notifications & Reminders',
    'Priority Customer Support',
    'Early Access to New Features',
    'Data Export & Backup',
    'Team Collaboration Tools'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      // Validate payment details
      if (!paymentData.cardNumber || !paymentData.expiryDate || 
          !paymentData.cvv || !paymentData.name) {
        throw new Error('Please fill in all payment details');
      }

      // Add payment confirmation section
      setShowConfirmation(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubscribe = async () => {
    setIsProcessing(true);
    try {
      // Subscription logic here
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulated delay
      navigate('/dashboard');
    } catch (error) {
      console.error('Subscription error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReturnToFree = () => {
    navigate('/dashboard');
  };

  const handleConfirmPayment = async () => {
    setIsProcessing(true);
    try {
      // Process payment here
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulated delay
      
      // Update user subscription status
      // Navigate to success page or show success message
      alert('Payment successful! Welcome to Pro!');
      navigate('/dashboard');
    } catch (error) {
      setError('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpgradeClick = () => {
    setShowPaymentModal(true);
  };

  return (
    <Layout>
      <Container>
        <Title>Upgrade to Pro</Title>
        <Description>
          Take your subscription management to the next level with advanced features
          and unlimited access to all premium tools.
        </Description>

        <PlanCard>
          <PlanHeader>
            <PlanName>Pro Plan</PlanName>
            <PriceContainer>
              <Price>$8.00</Price>
              <Period>/month</Period>
            </PriceContainer>
          </PlanHeader>

          <FeatureList>
            {features.map((feature, index) => (
              <Feature key={index}>{feature}</Feature>
            ))}
          </FeatureList>

          <SubscribeButton 
            onClick={handleUpgradeClick}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'UPGRADE NOW'}
          </SubscribeButton>
          
          <ReturnButton onClick={handleReturnToFree}>
            Return to Free Plan
          </ReturnButton>
        </PlanCard>

        <AnimatePresence>
          {showPaymentModal && (
            <PaymentModal
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ModalContent
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
              >
                <CloseButton onClick={() => setShowPaymentModal(false)}>
                  ×
                </CloseButton>
                <PaymentDetails 
                  onClose={() => setShowPaymentModal(false)}
                  onSuccess={() => {
                    setShowPaymentModal(false);
                    navigate('/dashboard');
                  }}
                />
              </ModalContent>
            </PaymentModal>
          )}
        </AnimatePresence>

        {showConfirmation && (
          <PaymentConfirmation>
            <PaymentSummary>
              <h3>Payment Summary</h3>
              <p>
                <span>Plan:</span>
                <span>Pro Subscription</span>
              </p>
              <p>
                <span>Amount:</span>
                <span>$9.99/month</span>
              </p>
              <p>
                <span>Card:</span>
                <span>**** **** **** {paymentData.cardNumber.slice(-4)}</span>
              </p>
            </PaymentSummary>

            <ConfirmButton
              onClick={handleConfirmPayment}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Confirm Payment'}
            </ConfirmButton>
          </PaymentConfirmation>
        )}
      </Container>
    </Layout>
  );
};

export default ProSubscription; 